import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@pwc/appkit-react/lib/Button';
import { useQueryClient } from 'react-query';
import { SYNC_REQUEST_APPROVED_STATUS } from '@common-packages/shared-constants';

import { customerPermissionsSelector } from '../../shared/store/selectors';
import { taxYearSelector, periodSelector } from '../store/selectors';
import AgGrid from '../../shared/displayComponents/agGrid/agGrid.component';
import { HeaderWithParamDropdownsWrapper } from '../../shared/displayComponents/headerWithParamDropdowns';
import { QueryKeys } from '../../shared/queries';
import { useQueryContentDeployments } from '../../shared/queries/contentDeployment';
import {
  useMutationCreateDeployment,
  useMutationDeploy,
} from '../../shared/mutations/contentDeployment';
import useModal from '../../shared/hooks/useModal.hook';

import getClientColumnDefinitions from './deploymentClient.columnDefinitions';
import { normalizeClientId } from './deploymentClientCheckbox.component';
import requestColumnDefinitions from './deploymentRequest.columnDefinitions';
import ContentDeploymentWarningModal from './contentDeploymentWarningModal.component';
import {
  hasPermissionToAuthorizeContentDeployment,
  hasPermissionToDeployContent,
} from './permissions';

const normalizeKey = value =>
  String(value ?? '')
    .trim()
    .toUpperCase();

const haveSameIds = (leftIds, rightIds) =>
  leftIds.length === rightIds.length && leftIds.every((id, index) => id === rightIds[index]);

const ContentDeployments = () => {
  const queryClient = useQueryClient();
  const taxYear = useSelector(taxYearSelector);
  const period = useSelector(periodSelector);
  const customerPermissions = useSelector(customerPermissionsSelector);
  const [selectedClientIds, setSelectedClientIds] = useState([]);

  const hasPermissionToAuthorize = hasPermissionToAuthorizeContentDeployment(customerPermissions);
  const hasPermissionToDeploy = hasPermissionToDeployContent(customerPermissions);

  const { showModal, modalProps } = useModal();

  const { data: deployments, isFetching: isFetchingDeployments } = useQueryContentDeployments({
    params: { taxYear, period },
    enabled: Boolean(taxYear) && Boolean(period),
    onSuccess: () => setSelectedClientIds([]),
  });

  const selectableClientIds = useMemo(
    () =>
      (deployments?.clients || [])
        .filter(client => !Number(client.deploymentAuthorized))
        .map(client => normalizeClientId(client.id ?? client.clientId))
        .filter(Boolean),
    [deployments?.clients],
  );

  const clientRowData = useMemo(
    () =>
      (deployments?.clients || []).map(client => {
        const clientId = normalizeClientId(client.id ?? client.clientId);

        return {
          ...client,
          isClientSelectable: !Number(client.deploymentAuthorized),
          isSelected: clientId ? selectedClientIds.includes(clientId) : false,
        };
      }),
    [deployments?.clients, selectedClientIds],
  );

  useEffect(() => {
    setSelectedClientIds(previousIds => {
      const nextIds = previousIds.filter(id => selectableClientIds.includes(normalizeClientId(id)));

      return haveSameIds(previousIds, nextIds) ? previousIds : nextIds;
    });
  }, [selectableClientIds]);

  const isDeploymentApprovedForTaxYearAndPeriod = useMemo(
    () =>
      deployments?.requests?.some(
        ({ overallStatus }) => normalizeKey(overallStatus) === SYNC_REQUEST_APPROVED_STATUS,
      ),
    [deployments],
  );

  const onToggleClient = useCallback(clientId => {
    const normalizedId = normalizeClientId(clientId);

    if (!normalizedId) {
      return;
    }

    setSelectedClientIds(previousIds =>
      previousIds.includes(normalizedId)
        ? previousIds.filter(id => id !== normalizedId)
        : [...previousIds, normalizedId],
    );
  }, []);

  const onToggleAll = useCallback(
    shouldSelect => {
      setSelectedClientIds(shouldSelect ? [...selectableClientIds] : []);
    },
    [selectableClientIds],
  );

  const clientColumnDefinitions = useMemo(
    () =>
      getClientColumnDefinitions({
        hasPermissionToAuthorize,
        selectableClientIds,
        selectedClientIds,
        onToggleClient,
        onToggleAll,
      }),
    [hasPermissionToAuthorize, selectableClientIds, selectedClientIds, onToggleClient, onToggleAll],
  );

  const {
    mutateAsync: createDeployment,
    isLoading: isLoadingCreateDeployment,
  } = useMutationCreateDeployment({
    onSuccess: () => setSelectedClientIds([]),
  });

  const {
    data: responseData,
    mutateAsync: deploy,
    isLoading: isLoadingDeploy,
  } = useMutationDeploy();

  const onNewDeploymentClick = useCallback(() => {
    createDeployment({ taxYear, period, clientIds: selectedClientIds });
  }, [createDeployment, taxYear, period, selectedClientIds]);

  const getClientRowId = useCallback(
    ({ data }) => normalizeClientId(data.id ?? data.clientId) ?? '',
    [],
  );

  const onDeployClick = useCallback(() => {
    deploy({ taxYear, period });
    showModal();
  }, [deploy, showModal, taxYear, period]);

  const onRefreshClick = useCallback(() => {
    queryClient.resetQueries(QueryKeys.ContentDeployment.ContentDeployments);
  }, [queryClient]);

  const isLoading = isFetchingDeployments || isLoadingCreateDeployment || isLoadingDeploy;

  return (
    <>
      <HeaderWithParamDropdownsWrapper showHeaderTaxYearDropdown showHeaderPeriodDropdown />
      <div className="row">
        <div className="col-2 add-button-column">
          <div>
            {hasPermissionToAuthorize && (
              <Button
                size="lg"
                className="add-button"
                onClick={onNewDeploymentClick}
                disabled={!hasPermissionToAuthorize || isLoading || !selectedClientIds.length}
              >
                Authorize Deployment
              </Button>
            )}
          </div>
        </div>
        <div className="col-10 add-button-column">
          {hasPermissionToDeploy && (
            <Button
              size="lg"
              className="add-button"
              onClick={onDeployClick}
              disabled={isLoading || !isDeploymentApprovedForTaxYearAndPeriod}
            >
              Deploy
            </Button>
          )}
          <Button size="lg" className="add-button" onClick={onRefreshClick} disabled={isLoading}>
            Refresh
          </Button>
        </div>
      </div>
      <div className="row grid-row">
        <div className="col-2">
          <AgGrid
            rowData={clientRowData}
            columnDefs={clientColumnDefinitions}
            isGridLoading={isLoading}
            withSearchBar
            areHeaderCellBordersEnabled
            getRowId={getClientRowId}
            autoMaxWidth
          />
        </div>
        <div className="col-10">
          <AgGrid
            rowData={deployments?.requests}
            columnDefs={requestColumnDefinitions}
            isGridLoading={isLoading}
            withSearchBar
            areHeaderCellBordersEnabled
          />
        </div>
      </div>
      <ContentDeploymentWarningModal
        isLoading={isLoading}
        data={responseData?.clientErrors}
        {...modalProps}
      />
    </>
  );
};

export default ContentDeployments;
